/**
 * Currency conversion utility.
 * Base currency: MKD (Macedonian Denar)
 * Exchange rates are approximate fixed rates for display purposes.
 * Only mk stays in MKD; every other language uses its local currency.
 */

// How many MKD equals 1 unit of the target currency
// Rates tuned so that 1990 MKD rounds (Math.ceil) to a clean display price:
//   USD → $35, EUR → €32, ALL → 4.000 L, TRY → ₺1.400, RSD → 3.700 din.
const EXCHANGE_RATES = {
  USD: 56.86,   // 1990 MKD → $35
  EUR: 62.2,    // 1990 MKD → €32
  ALL: 0.4975,  // 1990 MKD → 4.000 L
  TRY: 1.422,   // 1990 MKD → ₺1.400
  RSD: 0.538,   // 1990 MKD → 3.700 din.
};

// Which currency to show per language (only mk keeps MKD)
const LANG_TO_CURRENCY = {
  mk: 'MKD',
  al: 'ALL',
  tr: 'TRY',
  sr: 'RSD',
  hr: 'EUR',
  en: 'USD',
  de: 'EUR',
};

// Symbol and whether it appears before the amount
const CURRENCY_META = {
  MKD: { symbol: 'МКД', isPrefix: false },
  USD: { symbol: '$',   isPrefix: true  },
  EUR: { symbol: '€',   isPrefix: true  },
  ALL: { symbol: 'L',   isPrefix: false },
  TRY: { symbol: '₺',   isPrefix: true  },
  RSD: { symbol: 'din.', isPrefix: false },
};

export const getCurrency = (lang) => LANG_TO_CURRENCY[lang] || 'MKD';

/**
 * Convert an MKD amount to the display currency for the given language.
 * Rounds up to the nearest integer (Math.ceil) for non-MKD currencies.
 */
export const convertFromMKD = (mkdAmount, lang) => {
  const currency = getCurrency(lang);
  if (currency === 'MKD') return mkdAmount;
  return Math.ceil(mkdAmount / EXCHANGE_RATES[currency]);
};

/** Returns the currency symbol for a language. */
export const getCurrencySymbol = (lang) => {
  return CURRENCY_META[getCurrency(lang)].symbol;
};

/** Returns true if the symbol should appear before the amount (e.g. $, €, ₺). */
export const isCurrencyPrefix = (lang) => {
  return CURRENCY_META[getCurrency(lang)].isPrefix;
};

/**
 * Format an MKD amount as a display string for the given language.
 * e.g. formatPrice(1990, 'en') → '$35'
 *      formatPrice(1990, 'de') → '€33'
 *      formatPrice(1990, 'al') → '3980 L'
 *      formatPrice(1990, 'tr') → '₺1393'
 *      formatPrice(1990, 'sr') → '3784 din.'
 *      formatPrice(1990, 'hr') → '€33'
 *      formatPrice(1990, 'mk') → '1.990 МКД'
 */
export const formatPrice = (mkdAmount, lang) => {
  const amount = convertFromMKD(mkdAmount, lang);
  const meta = CURRENCY_META[getCurrency(lang)];
  // Format with dot as thousands separator
  const formatted = amount >= 1000
    ? `${Math.floor(amount / 1000)}.${String(amount % 1000).padStart(3, '0')}`
    : String(amount);
  if (meta.isPrefix) {
    return `${meta.symbol}${formatted}`;
  }
  return `${formatted} ${meta.symbol}`;
};
